/**
 * @module imgix
 */
const { escapeUrl } = require('./urls');
const { toType } = require('../toType');

/**
 * @typedef {object} MgnlEnvVars
 * @property {string} IMGIX_BASE_URL -Imgix base url (e.g. 'https://cms-images.life.church').
 */

/**
 * Convenience function to determine whether or not the specified value is an Imgix url.
 *
 * @param {string} src - The source string value to check.
 * @param {string} imgixBaseUrl - Base URL for Imgix assets. (Default: 'https://cms-images.life.church').
 *
 * @returns {boolean} Boolean flag denoting whether or not the source string is an Imgix url.
 */
function checkIfIsImgixUrl(
  src,
  imgixBaseUrl = 'https://cms-images.life.church',
) {
  const baseUrl = 'https://SUB.life.church';
  const supportedImgixBaseUrls = [
    baseUrl.replace('SUB', 'campus-images'),
    baseUrl.replace('SUB', 'cms-images'),
    baseUrl.replace('SUB', 'finds-images'),
  ];
  return (
    src &&
    (src.indexOf(imgixBaseUrl) > -1 || supportedImgixBaseUrls.includes(src))
  );
}

/**
 * Convenience function to convert values in the specified JSON to S3 AWS values.
 *
 * @param {string} json - The JSON string to parse, check, replace, and correct S3 url values.
 * @param {string} imgixBaseUrl - Base URL for Imgix assets.
 * @param {string} bucketName - Optional AWS bucket name against which to search/replace. (Default: 'magnolia-cms').
 *
 * @returns {string} The converted JSON string.
 */
function convertS3DamItems(json, imgixBaseUrl, bucketName = 'magnolia-cms') {
  return (
    (imgixBaseUrl &&
      JSON.parse(
        JSON.stringify(json)
          .replace(
            new RegExp(`s3:${bucketName}-(uat|live)`, 'g'),
            (_match, p1) => `https://${bucketName}-${p1}.s3.amazonaws.com`,
          )
          .replace(
            new RegExp(
              `https://${bucketName}-(uat|live).s3.amazonaws.com`,
              'g',
            ),
            imgixBaseUrl,
          ),
      )) ||
    JSON.parse(
      JSON.stringify(json).replace(
        new RegExp(`s3:${bucketName}-(uat|live)`, 'g'),
        (_match, p1) => `https://${bucketName}-${p1}.s3.amazonaws.com`,
      ),
    )
  );
}

/**
 * Convenience function to get the logo path based on the collection type.
 *
 * Note: Since there are parts still pulling data from v1 it is possible this function will receive types with underscore.
 *
 * @param {string} type - The media collection type.
 * @param {string} imgixBaseUrl - Base URL for Imgix assets. (Default: 'https://cms-images.life.church').
 *
 * @returns {string} The logo path of the specified collection type.
 */
function getLogoPathBasedOnCollectionType(
  type,
  imgixBaseUrl = 'https://cms-images.life.church',
) {
  let logoPath = '';
  const s3LogoPath = `${imgixBaseUrl}/logos/`;
  if (type === 'early_childhood' || type === 'early-childhood') {
    logoPath = `${s3LogoPath}early-childhood-logo`;
  } else if (type === 'switch' || type === 'loop' || type === 'konnect') {
    logoPath = `${s3LogoPath + type}-logo`;
  }
  return logoPath;
}

/**
 * Convenience function to replace Magnolia asset values with Imgix values in the specified string.
 *
 * @param {object|string} string - The value to inspect and replace Magnolia asset values with Imgix values.
 * @param {string} imgixBaseUrl - Base URL for Imgix assets. (Default: 'https://cms-images.life.church').
 * @param {string} bucketName - Optional AWS bucket name against which to search/replace. (Default: 'magnolia-cms').
 *
 * @returns {string} The string with Magnolia assets converted to Imgix values.
 */
async function replaceMagnoliaAssetsToImgix(
  string,
  imgixBaseUrl = 'https://cms-images.life.church',
  bucketName = 'magnolia-cms',
) {
  let returnValue = string;
  if (!returnValue) {
    return '';
  }
  if (toType(returnValue) !== 'string' && returnValue['@id']) {
    returnValue = returnValue['@id'];
  }
  returnValue = decodeURI(returnValue);
  returnValue = returnValue
    .replace(
      new RegExp(`s3:${bucketName}-(uat|live)`, 'g'),
      (_m, p1) => `https://${bucketName}-${p1}.s3.amazonaws.com`,
    )
    .replace(
      new RegExp(`https://${bucketName}-(uat|live).s3.amazonaws.com`, 'g'),
      imgixBaseUrl,
    );
  if (returnValue.includes(`undefined`)) {
    return escapeUrl(encodeURI(returnValue));
  }
  return escapeUrl(encodeURI(returnValue));
}

/**
 * Convenience function to replace Magnolia and Imgix asset values with CMS asset values in the specified string.
 *
 * @param {object} params - The function params object.
 * @param {string} params.bucketName - Optional AWS bucket name against which to search/replace. (Default: 'magnolia-cms').
 * @param {string} params.cmsAssetsBaseUrl - Base URL for CMS assets.
 * @param {string} params.imgixBaseUrl - Base URL for Imgix assets.
 * @param {object|string} params.source - The source value to inspect and replace Magnolia and Imgix asset values with CMS asset values.
 *
 * @returns {string} The string with Magnolia assets converted to CMS asset values.
 */
async function convertToCmsAssetUrl({
  bucketName = 'magnolia-cms',
  cmsAssetsBaseUrl,
  imgixBaseUrl,
  source,
} = {}) {
  if (!cmsAssetsBaseUrl || !imgixBaseUrl || !source) {
    return source || '';
  }
  let returnValue = source;
  if (toType(returnValue) !== 'string' && returnValue['@id']) {
    returnValue = returnValue['@id'];
  }
  returnValue = decodeURI(returnValue);
  returnValue = returnValue
    .replace(
      new RegExp(`s3:${bucketName}-(uat|live)`, 'g'),
      (_m, p1) => `https://${bucketName}-${p1}.s3.amazonaws.com`,
    )
    .replace(
      new RegExp(`https://${bucketName}-(uat|live).s3.amazonaws.com`, 'g'),
      cmsAssetsBaseUrl,
    )
    .replace(imgixBaseUrl, cmsAssetsBaseUrl);
  if (returnValue.includes(`undefined`)) {
    return escapeUrl(encodeURI(returnValue));
  }
  return escapeUrl(encodeURI(returnValue));
}

module.exports = {
  checkIfIsImgixUrl,
  convertS3DamItems,
  convertToCmsAssetUrl,
  getLogoPathBasedOnCollectionType,
  replaceMagnoliaAssetsToImgix,
};
